import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowRight, Heart, Shield } from 'lucide-react';

export default function AssessmentResults() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const r = sessionStorage.getItem('bp_last_result');
    if (r) setResult(JSON.parse(r));
    else nav('/assessment');
  }, [nav]);

  if (!result) return null;
  const domains = Object.entries(result.domain_scores);

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6">
      <div className="max-w-md mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Your snapshot</p>
        <h1 className="font-heading text-4xl mb-2">Here&apos;s where you are.</h1>
        <p className="text-navy/60 mb-6">A starting point, not a verdict.</p>

        <div data-testid="wellness-score-card" className="bg-navy text-white rounded-3xl p-8 mb-6 card-soft">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Wellness Score</p>
          <div className="font-heading text-6xl font-light">{result.wellness_score}</div>
          <div className="text-white/60 text-sm mt-2">out of 100</div>
        </div>

        <div className="bg-white rounded-3xl p-6 card-soft mb-6">
          <h3 className="font-heading text-lg mb-4">Your domains</h3>
          <div className="space-y-3">
            {domains.map(([id, d]) => (
              <div key={id} data-testid={`domain-${id}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{d.name}</span>
                  <span className="text-navy/60 text-xs">{d.label}</span>
                </div>
                <div className="h-2 bg-beige rounded-full overflow-hidden">
                  <div className="h-full bg-gold" style={{width: `${(d.score/6)*100}%`}}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-3xl p-5 card-soft">
            <p className="text-xs uppercase tracking-widest text-navy/50">Top concern</p>
            <p className="font-heading text-lg mt-1">{result.top_concern.name}</p>
          </div>
          <div className="bg-white rounded-3xl p-5 card-soft">
            <p className="text-xs uppercase tracking-widest text-navy/50">Strength</p>
            <p className="font-heading text-lg mt-1">{result.strength.name}</p>
          </div>
        </div>

        <div className="bg-beige rounded-3xl p-6 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-navy/60 font-semibold mb-2">Recommended next step</p>
          <p className="font-heading text-xl mb-4">{result.next_step}</p>
          <Link to={user ? `/app/plans/${result.recommended_plan_id}` : '/register'} data-testid="results-start-plan"
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-navy-soft transition-smooth">
            Start plan <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="space-y-3 mb-6">
          <Link to={user ? '/app/coach' : '/register'} data-testid="results-open-coach"
            className="flex items-center justify-between bg-white rounded-2xl p-4 card-soft hover:bg-white">
            <div className="flex items-center gap-3"><Heart className="text-gold" size={20} strokeWidth={1.5}/><span>Talk with AI Coach</span></div>
            <ArrowRight size={16} className="text-navy/40"/>
          </Link>
          <Link to={user ? '/app/therapists' : '/register'} data-testid="results-find-therapist"
            className="flex items-center justify-between bg-white rounded-2xl p-4 card-soft">
            <div className="flex items-center gap-3"><Shield className="text-green" size={20} strokeWidth={1.5}/><span>Find a therapist</span></div>
            <ArrowRight size={16} className="text-navy/40"/>
          </Link>
        </div>

        {!user && (
          <Link to="/register" data-testid="results-signup"
            className="block text-center bg-gold text-white rounded-full py-4 font-medium hover:bg-gold-hover transition-smooth">
            Save your results — create account
          </Link>
        )}
        {user && (
          <Link to="/app" data-testid="results-go-home"
            className="block text-center bg-gold text-white rounded-full py-4 font-medium hover:bg-gold-hover transition-smooth">
            Go to my dashboard
          </Link>
        )}
        <p className="text-xs text-navy/50 mt-6 text-center">Not diagnostic. Not therapy.</p>
      </div>
    </div>
  );
}
